import { useState } from 'react';
import { Link } from 'react-router-dom';
import { line, Color, RECORD_ROUTES, Size, previewRatePercent, t, type SiteTaxJurisdictionAnswer } from '@bms2/shared';
import { Button, Pill } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { LoadFailure } from '../../components/composites';
import { taxFormPath } from '../accounting/api';
import { post, useApi } from './api';
import { siteJurisdictionConfirmPath, siteJurisdictionPath } from './localTax';
import styles from './SiteTaxJurisdiction.module.css';

export interface SiteTaxJurisdictionPanelProps {
  siteId: string;
  apiBaseUrl?: string;
}

const COVERAGE_KEY = { None: 'site.taxJurisdiction.coverageNone', Partial: 'site.taxJurisdiction.coveragePartial', Complete: 'site.taxJurisdiction.coverageComplete' } as const;
const WARNING_KEY = {
  'no-state': 'site.taxJurisdiction.warning.noState',
  'county-not-entered': 'site.taxJurisdiction.warning.countyNotEntered',
  'jurisdiction-not-confirmed': 'site.taxJurisdiction.warning.notConfirmed',
  'address-estimated': 'site.taxJurisdiction.warning.addressEstimated',
} as const;

/**
 * 🔒 The site page's Tax Jurisdiction (decision 0739; the product owner,
 * 2026-09-25: *"the site location needs county added then the tax is looked up
 * then there is indicator of tax jurisdiction on site page"*). It reads the
 * route and draws what it answers. 🔒 It names each form the site needs with its
 * link and its On File or Not on File pill, the state's own fact; recording a
 * certificate and the forms library are in the Exempt Customer Steps window
 * (decision 2851).
 */
export function SiteTaxJurisdictionPanel({ siteId, apiBaseUrl = '' }: SiteTaxJurisdictionPanelProps) {
  const fetched = useApi<SiteTaxJurisdictionAnswer>(siteJurisdictionPath(siteId), apiBaseUrl);
  const [message, setMessage] = useState<{ tone: 'good' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  // 🔨 A reader the route refuses sees nothing here, the way the site's other gated panels read.
  if (fetched.denied) return null;
  if (fetched.state.kind === 'failed') return <LoadFailure state={fetched.state} onRetry={fetched.reload} />;
  if (fetched.state.kind !== 'ready') return <p className={styles.muted}>{t('site.taxJurisdiction.loading')}</p>;
  const confirm = async () => {
    setBusy(true);
    const answer = await post<SiteTaxJurisdictionAnswer>(siteJurisdictionConfirmPath(siteId), {}, apiBaseUrl);
    setBusy(false);
    setMessage(answer.ok ? { tone: 'good', text: t('site.taxJurisdiction.confirmed') } : { tone: 'error', text: answer.message });
    fetched.reload();
  };
  return <SiteTaxJurisdictionView answer={fetched.state.data} busy={busy} message={message} onDismiss={() => setMessage(null)} onConfirm={() => void confirm()} apiBaseUrl={apiBaseUrl} />;
}

export interface SiteTaxJurisdictionViewProps {
  answer: SiteTaxJurisdictionAnswer;
  busy?: boolean;
  message?: { tone: 'good' | 'error'; text: string } | null;
  onDismiss?: () => void;
  onConfirm?: () => void;
  apiBaseUrl?: string;
}

/** The drawing alone, over an answer, so the Component Test Page draws each state with no route behind it. */
export function SiteTaxJurisdictionView({ answer, busy = false, message = null, onDismiss, onConfirm, apiBaseUrl = '' }: SiteTaxJurisdictionViewProps) {
  // 🔨 An answer missing a list reads as an empty one, never a crash of the site page.
  const rows = Array.isArray(answer.rows) ? answer.rows : [];
  const forms = Array.isArray(answer.forms) ? answer.forms : [];
  const certificates = Array.isArray(answer.certificates) ? answer.certificates : [];
  const scheduled = Array.isArray(answer.scheduled) ? answer.scheduled : [];
  // 🔨 A municipality whose own form is listed shows its certificate beside that form, so its line of its own is drawn only where it names none (decision 0739).
  const municipalLines = certificates.filter((row) => row.municipality && !forms.some((form) => form.jurisdiction === row.jurisdiction));
  const place = line(answer.stateName ?? '', answer.county ? '·' : '', answer.county ?? '', answer.town ? '·' : '', answer.town ?? '');
  const locationRoute = answer.locationId ? RECORD_ROUTES.LocationRecord.record.replace(':id', answer.locationId) : null;
  return (
    <div className={styles.panel} data-site-tax-jurisdiction={answer.siteId} data-tax-warning={answer.warning ?? 'none'}>
      {message ? <AlertBanner tone={message.tone} message={message.text} {...(onDismiss ? { action: { label: t('modal.dismiss'), onAction: onDismiss } } : {})} /> : null}
      {answer.warning ? (
        <div className={styles.warning} data-tax-jurisdiction-warning={answer.warning}>
          <span>{t(WARNING_KEY[answer.warning])}</span>
          {answer.warning === 'county-not-entered' && locationRoute ? <Link to={locationRoute} data-tax-jurisdiction-location="true">{t('site.taxJurisdiction.enterCounty')}</Link> : null}
          {answer.mayConfirm && onConfirm ? <Button size={Size.Small} color={Color.Navy} text={[t('site.taxJurisdiction.confirm')]} actions={[onConfirm]} disabled={busy} /> : null}
        </div>
      ) : null}
      {place ? <p className={styles.place} data-tax-jurisdiction-place="true">{place}</p> : null}
      {rows.length > 0 ? (
        <dl className={styles.rates}>
          {rows.map((row) => (
            <div key={`${row.level}-${row.label}`} className={styles.rate} data-tax-jurisdiction-row={row.level}>
              <dt>{row.label}</dt>
              <dd>{row.ratePercent === null ? t('site.taxJurisdiction.noRate') : previewRatePercent(row.ratePercent)}</dd>
            </div>
          ))}
          <div className={`${styles.rate} ${styles.combined}`} data-tax-jurisdiction-row="combined">
            <dt>{t('site.taxJurisdiction.combined')}</dt>
            <dd>{answer.combinedRatePercent === null ? t('site.taxJurisdiction.noRate') : previewRatePercent(answer.combinedRatePercent)}</dd>
          </div>
        </dl>
      ) : null}
      {/* 🔒 A jurisdiction's next rate where it starts after today, outside the combined rate, which is today's (decision 0739). */}
      {scheduled.map((row) => (
        <p key={`scheduled-${row.level}-${row.label}`} className={styles.muted} data-tax-jurisdiction-scheduled={row.level}>
          {line(row.label, previewRatePercent(row.ratePercent), t('site.taxJurisdiction.from'), row.effectiveFrom, '·', t('site.taxJurisdiction.scheduled'))}
        </p>
      ))}
      {answer.coverage ? (
        <p className={styles.muted} data-tax-jurisdiction-coverage={answer.coverage}>
          {t(COVERAGE_KEY[answer.coverage])}
          {answer.confirmedOn ? ` ${line(t('site.taxJurisdiction.confirmedOn'), answer.confirmedOn)}` : ''}
        </p>
      ) : null}
      {answer.stateCode && !answer.registered && answer.warning !== 'no-state' ? <p className={styles.muted} data-tax-jurisdiction-registered="false">{t('site.taxJurisdiction.notRegistered')}</p> : null}
      {forms.length > 0 ? (
        <div className={styles.forms} data-tax-jurisdiction-forms="true">
          <span className={styles.heading}>{t('site.taxJurisdiction.forms')}</span>
          {forms.map((form) => {
            const onFile = certificates.find((row) => row.jurisdiction === form.jurisdiction);
            return (
              <div key={`${form.jurisdiction}-${form.form}`} className={styles.form} data-tax-jurisdiction-form={form.jurisdiction}>
                <span>{line(form.jurisdiction, '·')}</span>
                {form.files.length > 0
                  ? form.files.map((file) => <a key={file.file} href={`${apiBaseUrl}${taxFormPath(file.file)}`} target="_blank" rel="noopener noreferrer" data-tax-jurisdiction-form-copy={file.file}>{file.title}</a>)
                  : form.url ? <a href={form.url} target="_blank" rel="noopener noreferrer">{form.form}</a> : <span>{form.form}</span>}
                {onFile ? (
                  <Pill
                    text={onFile.onFile ? t('site.taxJurisdiction.onFile') : t('site.taxJurisdiction.notOnFile')}
                    foreground={onFile.onFile ? 'var(--status-good-text)' : 'var(--status-warning-text)'}
                    background={onFile.onFile ? 'var(--status-good-bg)' : 'var(--status-warning-bg)'}
                    size={Size.Small}
                  />
                ) : null}
              </div>
            );
          })}
          {municipalLines.map((row) => (
            <div key={`municipality-${row.municipality}`} className={styles.form} data-tax-jurisdiction-municipality={row.municipality ?? ''}>
              <span>{line(row.jurisdiction, '·', t('site.taxJurisdiction.municipalCertificate'))}</span>
              <Pill text={row.onFile ? t('site.taxJurisdiction.onFile') : t('site.taxJurisdiction.notOnFile')} foreground={row.onFile ? 'var(--status-good-text)' : 'var(--status-warning-text)'} background={row.onFile ? 'var(--status-good-bg)' : 'var(--status-warning-bg)'} size={Size.Small} />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
